<script setup lang="ts">
import { computed, inject, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { NBackTop, NButton, NResult, NSkeleton } from 'naive-ui'
import { storeToRefs } from 'pinia'
import { useWikiPagesStore } from '../stores/wiki_pages'
import { useSessionStore } from '../stores/session'
import { useWikiMarkdown } from '../composables/useWikiMarkdown'
import { useDocumentTitle } from '../composables/useDocumentTitle'
import { useInternalLinkClicks } from '../composables/useInternalLinkClicks'
import { usePageSelection } from '../composables/usePageSelection'
import { stripRelationsBlock } from '../composables/stripRelationsBlock'
import CategoryView from './CategoryView.vue'
import MemoryView from './MemoryView.vue'
import MemoryEntryPage from './MemoryEntryPage.vue'
import HomeLanding from './HomeLanding.vue'
import EmptyWikiOnboarding from './EmptyWikiOnboarding.vue'
import RouteShell, { routeShellDrawersKey } from './RouteShell.vue'
import WikiPageMeta from './WikiPageMeta.vue'
import WikiPageRelations from './WikiPageRelations.vue'
import WikiSidebar from './WikiSidebar.vue'

const route = useRoute()

const wikiPages = useWikiPagesStore()
const session = useSessionStore()
const { pages, loaded, needsAuth, loadError } = storeToRefs(wikiPages)
const { currentUser } = storeToRefs(session)
const wikiMarkdown = useWikiMarkdown()

// Selection state + deep-link resolution + the live-update watcher
// live in their own composable so this view stays focused on
// the catalog + page rendering. Header / drawers / palette wiring
// moved to RouteShell.
const {
  selectedId,
  selectedDetail,
  loadingContent,
  deepLinkMissing,
  welcomeVisible,
  sidebarHiddenMobile,
  navigateToPageById,
  navigateToPageByPath,
  showSidebarOnMobile,
  hideSidebarOnMobile,
  selectDeepLinkedPage,
  clearSelection,
} = usePageSelection(pages)

const mainEl = ref<HTMLElement | null>(null)

// `/c/:category` is the breadcrumb's category-crumb destination.
// Resolves only when the matched route is `wikiCategory` so an
// accidental `?category=` on a wiki-page URL doesn't hijack the
// detail pane. Lower-cased on-disk form (matches WikiPage.category).
const categoryParam = computed<string | null>(() => {
  if (route.name !== 'wikiCategory') return null
  const raw = route.params.category
  const value = Array.isArray(raw) ? raw[0] : raw
  return typeof value === 'string' && value.trim() ? value.trim().toLowerCase() : null
})

// `/memory` routes through this shell so the sidebar stays
// visible — same pattern as `/c/:category`. Branches the main pane onto
// MemoryView. Admin gating is enforced by the route's `requiresAdmin`
// meta in the beforeEach guard, so this view trusts it.
const memoryRoute = computed(() => route.name === 'memory')
const memoryEntryRoute = computed(() => route.name === 'memoryEntry')

// Strip the `## Relations` block before rendering so the article body
// doesn't repeat what `WikiPageRelations` already shows above it.
// Disk content is untouched — the strip is a render-time
// concern only; the editor and the API still see the full markdown.
const renderedHtml = computed(() =>
  selectedDetail.value
    ? wikiMarkdown.render(stripRelationsBlock(selectedDetail.value.content), selectedDetail.value.path)
    : '',
)

// Browser tab shows "<page title> · superbrain" when a wiki page is
// selected, falling back to the bare app name on the welcome / not-found
// states. Keeps tab history + cmd+tab switching legible.
// `/memory` owns its own title via MemoryView's useDocumentTitle;
// every other branch is titled here from the selected page / 404 state.
useDocumentTitle(computed(() => selectedDetail.value?.title ?? (deepLinkMissing.value ? 'Page not found' : null)))

// Admin-only affordances on the page meta-bar (Edit / Export / Delete).
// The backend enforces the same gate, so hiding these is strictly cosmetic.
const isAdmin = computed(() => currentUser.value?.admin === true)

const onArticleClick = useInternalLinkClicks()

// Inject is non-null in production (RouteShell wraps every authenticated
// route) but typed optional so unit-test mounts can stub it.
const drawers = inject(routeShellDrawersKey, undefined)
function openAddToWiki() {
  drawers?.openAddToWiki()
}

watch(needsAuth, (value) => {
  // Session went stale server-side (API returned 401). Re-check so App.vue's
  // auth gate flips currentUser to null and renders the sign-in form.
  if (value) session.fetchCurrentUser()
})

async function loadCatalog(): Promise<void> {
  try {
    await wikiPages.fetchAll()
  } catch {
    // fetchAll sets loadError for the view to surface a Retry button.
  }
  if (!needsAuth.value) wikiPages.subscribe()
  selectDeepLinkedPage()
}

loadCatalog()

// NBackTop's `listen-to` prop is typed `() => Document | HTMLElement`
// (no null), but `mainEl` is `HTMLElement | null` until the template
// ref binds on mount. `document.documentElement` is the safe fallback
// — it's a real HTMLElement that's always mounted, and on the brief
// pre-bind window NBackTop attaches there harmlessly. Once
// `mainEl.value` is non-null (which happens before NBackTop's first
// scroll-listener attach because v-if="selectedDetail" wins later in
// the lifecycle), the function returns the actual scroll container.
function resolveScrollTarget(): HTMLElement {
  return mainEl.value ?? document.documentElement
}

// Matches Naive's own NBackTop default but is named here so a future
// tuning lands in one place.
const BACK_TO_TOP_VISIBILITY_HEIGHT = 600
</script>

<template>
  <RouteShell show-sidebar :main-class="{ 'max-sm:hidden': !sidebarHiddenMobile }">
    <template #sidebar>
      <WikiSidebar
        :pages="pages"
        :loaded="loaded"
        :load-error="loadError"
        :selected-id="selectedId"
        :hidden-mobile="sidebarHiddenMobile"
        @select-page="navigateToPageById"
        @select-path="navigateToPageByPath"
        @retry-load="loadCatalog"
        @close="hideSidebarOnMobile"
      />
    </template>

    <!-- Detail pane content. Wrapped in a ref so NBackTop can target
         the actual scroll container (the shell's <main>) rather than
         falling back to window-scroll which never fires here. -->
    <div ref="mainEl">
      <!-- Article-shaped skeleton: a wide title bar plus a stack of
           body lines so the layout doesn't reflow when the real
           content lands. Width matches the article (no max-w-prose) so
           the skeleton doesn't snap to a narrower column right before
           the real content appears. -->
      <div v-if="loadingContent" class="pt-2">
        <NSkeleton text :repeat="1" class="!h-7 !w-1/3 !mb-4" />
        <NSkeleton text :repeat="6" class="!mb-2" />
      </div>

      <!-- Category landing — `/c/:category` from the meta-bar
           breadcrumb. Branches before the page-detail/welcome states
           so the view renders even when usePageSelection has nothing
           selected (which is the normal case for this route). -->
      <CategoryView v-else-if="categoryParam" :category="categoryParam" />

      <!-- Memory browse pane — `/memory` renders here so the
           sidebar stays put, same as the category branch above. -->
      <MemoryView v-else-if="memoryRoute" />
      <MemoryEntryPage v-else-if="memoryEntryRoute" />

      <template v-else-if="selectedDetail">
        <WikiPageMeta
          :page="selectedDetail"
          :is-admin="isAdmin"
          :show-back="sidebarHiddenMobile"
          @back="showSidebarOnMobile"
          @cleared="clearSelection"
        />
        <!-- Typed forward edges + mentioned-by backlinks, surfaced
             above the article so the reader can traverse the link
             graph without scrolling. The bottom `## Relations`
             markdown is stripped from the rendered body (see
             `renderedHtml` above) so this panel is the single place
             relations are shown. Hides itself when there are none. -->
        <WikiPageRelations :relations="selectedDetail.relations" />
        <!-- Article fills the main pane (no max-w-prose cap). -->
        <article class="wiki-markdown" v-html="renderedHtml" @click="onArticleClick" />
      </template>

      <template v-else-if="welcomeVisible">
        <EmptyWikiOnboarding @add-to-wiki="openAddToWiki" />
      </template>

      <template v-else-if="deepLinkMissing">
        <!-- Render the title inside #default as a real <h2> so screen
             readers + e2e role queries resolve a heading element.
             NResult's `title` prop ships the text in a styled <div>,
             which is fine visually but invisible to ARIA's heading
             role. Skipping the prop and styling our own h2 keeps the
             icon + footer chrome from NResult while restoring the
             semantic landmark. -->
        <NResult status="404" class="pt-8">
          <template #default>
            <h2 class="text-lg font-semibold text-white m-0 mb-2">Page not found</h2>
            <p class="text-white/60 m-0">
              The page
              <code class="font-mono text-white/80">{{ deepLinkMissing }}</code>
              doesn't exist in the brain. It may have been deleted, renamed, or the link may be stale.
            </p>
          </template>
          <template #footer>
            <RouterLink to="/" custom v-slot="{ navigate }">
              <NButton @click="navigate">Back to brain</NButton>
            </RouterLink>
          </template>
        </NResult>
      </template>

      <!-- Default `/` landing — replaces the bare "select a page"
           hint with a card grid of recently updated pages plus
           category chips. Falls through here when the catalog has
           pages, the user isn't on a specific deep-link, and there's
           nothing in flight. -->
      <HomeLanding v-else @open-catalog="showSidebarOnMobile" />

      <!-- "To top" affordance for long articles. Only mounts when a
           page is actually being read — the welcome / 404 / hint
           states fit on one screen and don't need it. `:listen-to`
           points at the article's scroll container (the shell's <main>). -->
      <NBackTop
        v-if="selectedDetail"
        :listen-to="resolveScrollTarget"
        :visibility-height="BACK_TO_TOP_VISIBILITY_HEIGHT"
        :bottom="24"
        :right="24"
      />
    </div>
  </RouteShell>
</template>
